import { Injectable, inject, effect } from '@angular/core';
import { HabitStore } from '@app/_stores';
import { HabitTimerService } from './habit-timer.service';
import { Habit, HabitTimerRecord } from '@app/_interfaces/habit.interface';
import { log } from '@app/_services/logger';
import { StorageService } from './storage.servive';
import { toTimestamp } from '@app/_services/date';

/**
 * Service to sync running timers between devices
 * Detects timers started on other devices and resumes them locally
 */
@Injectable({
  providedIn: 'root',
})
export class HabitTimerSyncService {
  private readonly habitStore = inject(HabitStore);
  private readonly timerService = inject(HabitTimerService);
  private readonly storageService = inject(StorageService);

  /**
   * Track timers we've already detected to avoid duplicate starts
   */
  private detectedTimers = new Set<string>();

  constructor() {
    // Monitor habit store for running timers from cloud sync
    effect(() => {
      const habitMap = this.habitStore.habitMap();
      this.detectAndResumeRunningTimers(habitMap.setups);
    });

    // Immediately check for running timers (in case HabitStore already loaded)
    // This handles the race condition where data loads before this service is instantiated
    const currentHabitMap = this.habitStore.habitMap();
    this.detectAndResumeRunningTimers(currentHabitMap.setups);

    log.info('HabitTimerSyncService', 'Initialized and checked for running timers', {
      setupCount: Object.keys(currentHabitMap.setups).length,
    });
  }

  /**
   * Detect running timers from cloud that aren't in local timer state
   * This handles timers started on other devices (mobile)
   */
  private detectAndResumeRunningTimers(setups: Record<string, Record<string, Habit>>) {
    const localTimers = this.timerService.timers();
    let runningTimersInCloud = 0;
    let localTimersCount = Object.keys(localTimers).length;

    // ====================================================================
    // CRITICAL FIX: Clear detectedTimers if local timers are empty
    // This handles logout/login scenario where localStorage is cleared
    // but detectedTimers Set persists in memory
    // ====================================================================
    if (localTimersCount === 0 && this.detectedTimers.size > 0) {
      this.detectedTimers.clear();
    }

    // Iterate through all habits
    for (const [setupId, dates] of Object.entries(setups)) {
      for (const [dateString, habit] of Object.entries(dates)) {
        const timerId = `${setupId}&&&${dateString}`;

        // Check if timer is running based on cloud data
        const isRunningInCloud = this.isTimerRunningInCloud(habit);

        if (isRunningInCloud) {
          runningTimersInCloud++;
        }

        // Check if we already have this timer locally
        const existsLocally = timerId in localTimers;

        // Check if we've already detected this timer
        const alreadyDetected = this.detectedTimers.has(timerId);

        if (isRunningInCloud && !existsLocally && !alreadyDetected) {
          // Resume this timer locally
          this.resumeTimerFromCloud(setupId, dateString, habit);

          // Mark as detected
          this.detectedTimers.add(timerId);
        } else if (!isRunningInCloud && existsLocally) {
          // Timer stopped in cloud but still running locally - stop it
          this.timerService.stopTimer(timerId);

          // Clean up detection tracking
          this.detectedTimers.delete(timerId);
        } else if (!isRunningInCloud && alreadyDetected) {
          // Timer was previously detected but now stopped, just clean up tracking
          this.detectedTimers.delete(timerId);
        }
      }
    }
  }

  /**
   * Check if a timer is running based on cloud data
   * Mirrors Flutter's isTimerRunning logic
   */
  private isTimerRunningInCloud(habit: Habit): boolean {
    if (!habit.timerAnswer || habit.timerAnswer.length === 0) {
      return false;
    }

    // Sort by startTimestamp to get the last record
    const sortedRecords = [...habit.timerAnswer].sort((a, b) => {
      const aTime = toTimestamp(a.startedAt);
      const bTime = toTimestamp(b.startedAt);
      return aTime - bTime;
    });

    const lastRecord = sortedRecords[sortedRecords.length - 1];

    // Timer is running if last record has no endTimestamp OR endTimestamp is in the future
    if (lastRecord.endedAt === null) {
      return true;
    }

    const endTime = toTimestamp(lastRecord.endedAt);
    const now = Date.now();

    // If endTimestamp is in the future, timer is still running
    return endTime > now;
  }

  /**
   * Resume a timer that was started on another device
   */
  private resumeTimerFromCloud(setupId: string, dateString: string, habit: Habit) {
    const setup = this.habitStore.idToSetup()[setupId];
    if (!setup) {
      log.error('HabitTimerSyncService', 'Setup not found for timer', { setupId });
      return;
    }

    // Get the last timer record (the running one)
    const sortedRecords = [...(habit.timerAnswer || [])].sort((a, b) => {
      const aTime = toTimestamp(a.startedAt);
      const bTime = toTimestamp(b.startedAt);
      return aTime - bTime;
    });

    const runningRecord = sortedRecords[sortedRecords.length - 1];
    if (!runningRecord) {
      return;
    }

    // ====================================================================
    // CRITICAL FIX: Verify the record is actually running (endTimestamp is null)
    // If it's already completed, don't try to resume it!
    // ====================================================================
    if (runningRecord.endedAt !== null) {
      const endTime = toTimestamp(runningRecord.endedAt);
      const isInFuture = endTime > Date.now();

      if (!isInFuture) {
        log.warn('HabitTimerSyncService', 'Last timer record is already completed, not resuming', {
          setupId,
          dateString,
          recordId: runningRecord.id,
          startTimestamp: runningRecord.startedAt,
          endTimestamp: runningRecord.endedAt,
        });
        return; // Don't resume a completed timer!
      }
    }

    const timerId = `${setupId}&&&${dateString}`;

    // CRITICAL FIX: Use toTimestamp to handle Firestore timestamp conversion
    const startTimestampMs = toTimestamp(runningRecord.startedAt);
    const startTimestamp = new Date(startTimestampMs);

    // Calculate total seconds for the goal
    const totalSeconds = setup.timerGoal
      ? this.timerService.parseMinutes(setup.timerGoal).totalSeconds
      : 0;

    // Calculate elapsed time
    const now = new Date();
    const elapsedMs = now.getTime() - startTimestampMs;
    const elapsedSeconds = Math.floor(elapsedMs / 1000);

    // Get total seconds spent (all completed records + current elapsed)
    // CRITICAL: Only count records where endTimestamp is in the PAST
    // Flutter sets endTimestamp to future time (start + goal) for running timers!
    const nowMs = Date.now();
    const completedSeconds = this.timerService.getTotalSecondsSpent(
      habit.timerAnswer?.filter(r => {
        if (r.endedAt === null) return false; // Not completed
        const endTime = toTimestamp(r.endedAt);
        return endTime <= nowMs; // Only count if end time is in the past
      }) || []
    );
    const totalSecondsSpent = completedSeconds + elapsedSeconds;

    // Create local timer state
    const state = {
      id: timerId,
      setupId,
      dateString,
      startTimestamp: startTimestamp,
      totalSecondsSpent: totalSecondsSpent,
      totalSeconds: totalSeconds,
    };

    // Add to local timers
    const timers = { ...this.timerService.timers(), [timerId]: state };

    // ====================================================================
    // CRITICAL FIX: Save to localStorage (matches startTimer behavior)
    // Without this, timer is lost on page refresh or if component opens
    // before timer sync completes
    // ====================================================================
    this.storageService.setHabitTimers(timers);
    this.timerService.timers.set(timers);

    // Start the interval if not already running
    if (!this.timerService['timerTickInterval']) {
      this.timerService['runTimerInterval']();
    }
  }

  /**
   * Check if a timer should be stopped based on cloud data
   * Call this before stopping a timer locally
   */
  isTimerStoppedInCloud(setupId: string, dateString: string): boolean {
    const habit = this.habitStore.habitMap().setups[setupId]?.[dateString];
    if (!habit) {
      return false;
    }

    return !this.isTimerRunningInCloud(habit);
  }
}
